import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { QUEUE_ENTRY_POINTS, entryPointFor, type QueueEntryPoint } from '../../lib/agentPrompts';
import { fetchCurrentPrompts, type PromptVersion } from '../../lib/prompts';
import { supabase } from '../../lib/supabase';
import { taskTypeOf } from '../../lib/taskTypes';
import type { AiJob } from '../../lib/supabase.types';
import { useUrlParam } from '../../hooks/useUrlParam';
import { Badge, Card, SegmentedControl } from '../ui';
import PromptEditor, { type PromptState } from './PromptEditor';
import { promptPre, when } from './promptStyles';

type JobRow = Pick<AiJob, 'id' | 'kind' | 'title' | 'brief' | 'queue_note' | 'created_at' | 'status'>;

const faint: React.CSSProperties = { color: 'var(--text-faint)' };

/** The last 500 jobs, grouped by the button that made them. */
function useJobsByEntry() {
  const [byEntry, setByEntry] = useState<Map<string, JobRow[]> | null>(null);
  useEffect(() => {
    let cancelled = false;
    void supabase
      .from('ai_jobs')
      .select('id, kind, title, brief, queue_note, created_at, status')
      .order('created_at', { ascending: false })
      .limit(500)
      .then(({ data }) => {
        if (cancelled) return;
        const map = new Map<string, JobRow[]>();
        for (const j of (data ?? []) as JobRow[]) {
          const e = entryPointFor(j);
          map.set(e.id, [...(map.get(e.id) ?? []), j]);
        }
        setByEntry(map);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return byEntry;
}

type View = 'preview' | 'latest';

function EntryDetail({ entry, jobs, loaded }: { entry: QueueEntryPoint; jobs: JobRow[]; loaded: boolean }) {
  const latest = jobs[0] ?? null;
  const [viewParam, setViewParam] = useUrlParam('view');
  const [prompt, setPrompt] = useState<PromptState | null>(null);
  const onState = useCallback((s: PromptState) => setPrompt(s), []);
  const type = taskTypeOf(entry.taskType);

  // The preview renders the made-up sample through the button's own builder with the template being edited, or the
  // one in use. Buttons without a sample (the free-text queue) only have real jobs to show.
  const template = prompt ? prompt.draft ?? prompt.current : undefined;
  const waiting = !!entry.prompt && !prompt; // the saved template hasn't loaded yet
  const preview = useMemo(() => (entry.sample && !waiting ? entry.sample(template) : null), [entry, template, waiting]);
  const editing = prompt?.draft != null;
  const view: View = editing || viewParam === 'preview' || (viewParam !== 'latest' && !latest) ? 'preview' : latest ? 'latest' : 'preview';
  const sentBeforeEdit = !!(latest && prompt?.saved && latest.created_at < prompt.saved.created_at);

  const output = (
    <Card
      eyebrow={editing ? 'Preview of your draft · made-up data, nothing is sent' : 'What the agent reads first · the job’s queue note'}
      title={view === 'latest' && latest ? latest.title : 'Sample, rendered by the button’s own builder'}
      actions={
        latest && preview && !editing ? (
          <SegmentedControl
            value={view}
            onChange={(v) => setViewParam(v === 'latest' ? 'latest' : 'preview')}
            options={[
              { id: 'latest', label: 'Latest real' },
              { id: 'preview', label: 'Sample' },
            ]}
          />
        ) : null
      }
    >
      {view === 'latest' && latest ? (
        <div className="t-num" style={{ ...faint, fontSize: 'var(--text-xs)', marginBottom: '8px' }}>
          Sent {when(latest.created_at)}
          {sentBeforeEdit ? ', before the current prompt was saved' : ''} ·{' '}
          <Link to={`/agents?job=${latest.id}`} style={{ color: 'var(--accent-text)' }}>
            open the job
          </Link>
        </div>
      ) : null}
      {view === 'latest' && latest ? (
        <pre style={promptPre}>{latest.queue_note}</pre>
      ) : preview != null ? (
        <pre style={promptPre}>{preview}</pre>
      ) : (
        <span style={faint}>
          {entry.prompt ? 'Loading…' : 'No job has come from this button yet. There’s no canned prompt to preview: the note is whatever the person types.'}
        </span>
      )}
      {view === 'latest' && jobs.length > 1 ? (
        <div style={{ marginTop: 'var(--space-3)', display: 'flex', flexDirection: 'column', gap: '4px', fontSize: 'var(--text-xs)' }}>
          <span className="t-eyebrow">Earlier ones</span>
          {jobs.slice(1, 8).map((j) => (
            <Link key={j.id} to={`/agents?job=${j.id}`} style={{ color: 'var(--text-muted)', textDecoration: 'none' }}>
              <span className="t-num" style={faint}>
                {when(j.created_at)}
              </span>{' '}
              · {j.title}
            </Link>
          ))}
        </div>
      ) : null}
    </Card>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div>
        <Link to="/agents/prompts" style={{ fontSize: 'var(--text-sm)', color: 'var(--text-muted)', textDecoration: 'none' }}>
          ← All prompts
        </Link>
      </div>
      <Card
        eyebrow={entry.buildStep ? 'Build step' : 'Queue button'}
        title={entry.label}
        actions={entry.buildStep ? <Badge tone="neutral">once per section</Badge> : <Badge tone="neutral">{loaded ? `${jobs.length} job${jobs.length === 1 ? '' : 's'}` : '…'}</Badge>}
      >
        <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr)', gap: '8px 16px', fontSize: 'var(--text-sm)' }}>
          <dt style={faint}>Where</dt>
          <dd style={{ margin: 0 }}>{entry.where}</dd>
          <dt style={faint}>Creates</dt>
          <dd style={{ margin: 0 }}>
            <Link to={`/agents/task-types/${type.id}`} style={{ color: 'var(--accent-text)' }}>
              {type.label}
            </Link>{' '}
            <span style={faint}>· job kind {entry.kind}</span>
          </dd>
          <dt style={faint}>Skill</dt>
          <dd style={{ margin: 0 }} className="t-num">
            {entry.skill}
          </dd>
        </dl>
      </Card>

      {entry.prompt ? (
        <PromptEditor entry={entry as QueueEntryPoint & { prompt: NonNullable<QueueEntryPoint['prompt']> }} onState={onState}>
          {output}
        </PromptEditor>
      ) : (
        output
      )}
    </div>
  );
}

/**
 * Agents → Prompts: every button that sends work to the agent queue (src/lib/agentPrompts.ts) with the prompt it
 * writes. Prompts with a template are editable here, and the button sends the saved version from then on.
 * /agents/prompts/:promptId opens one; ?edit=1, ?version=<id> and ?view= keep its state in the URL.
 */
export default function PromptsView() {
  const { promptId } = useParams();
  const byEntry = useJobsByEntry();
  const [saved, setSaved] = useState<Map<string, PromptVersion> | null>(null);
  const entry = promptId ? QUEUE_ENTRY_POINTS.find((e) => e.id === promptId) : null;

  useEffect(() => {
    if (entry) return;
    let cancelled = false;
    fetchCurrentPrompts()
      .then((m) => !cancelled && setSaved(m))
      .catch(() => !cancelled && setSaved(new Map()));
    return () => {
      cancelled = true;
    };
  }, [entry]);

  if (entry) return <EntryDetail key={entry.id} entry={entry} jobs={byEntry?.get(entry.id) ?? []} loaded={!!byEntry} />;

  return (
    <Card eyebrow="Agent queue" title={`${QUEUE_ENTRY_POINTS.filter((e) => !e.buildStep).length} buttons send work to the agents`}>
      <div style={{ ...faint, fontSize: 'var(--text-sm)', marginBottom: 'var(--space-3)' }}>
        Open one to read or edit its prompt. A saved prompt is what the button sends from then on.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {QUEUE_ENTRY_POINTS.map((e) => {
          const jobs = byEntry?.get(e.id) ?? [];
          const mine = e.prompt ? saved?.get(e.prompt.id) : undefined;
          return (
            <Link
              key={e.id}
              to={`/agents/prompts/${e.id}`}
              // Phones: name and path on the full width, the badges and job count on a line under them.
              className="hover-wash flex-wrap md:flex-nowrap"
              style={{ display: 'flex', alignItems: 'center', gap: '6px 12px', padding: '10px', margin: '0 -10px', borderRadius: 'var(--radius-sm)', textDecoration: 'none', color: 'inherit' }}
            >
              <div className="basis-full md:basis-0" style={{ flexGrow: 1, flexShrink: 1, minWidth: 0 }}>
                <div style={{ fontSize: 'var(--text-sm)', color: 'var(--text-body)', fontWeight: 'var(--weight-medium)' as React.CSSProperties['fontWeight'] }}>{e.label}</div>
                <div style={{ ...faint, fontSize: 'var(--text-xs)' }}>
                  {e.where} · skill {e.skill}
                </div>
              </div>
              {!e.prompt ? (
                <Badge tone="idle">Typed by hand</Badge>
              ) : mine?.template ? (
                <Badge tone="accent">Edited {when(mine.created_at)}</Badge>
              ) : saved ? (
                <Badge tone="neutral">Default prompt</Badge>
              ) : null}
              <Badge tone={e.taskType === 'general' ? 'neutral' : 'accent'}>{taskTypeOf(e.taskType).label}</Badge>
              <span className="t-num md:w-[190px] md:text-right" style={{ ...faint, fontSize: 'var(--text-xs)', whiteSpace: 'nowrap' }}>
                {e.buildStep ? 'once per section at build' : byEntry ? (jobs.length ? `${jobs.length} job${jobs.length === 1 ? '' : 's'} · ${when(jobs[0].created_at)}` : 'not used yet') : '…'}
              </span>
            </Link>
          );
        })}
      </div>
    </Card>
  );
}
